import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import type { Order } from '@billx/types';
import {
  deleteQueryItem,
  updateQueryItem,
  upsertQueryItem,
  WS_EVENTS,
  WS_ROOM_TYPES,
} from '@billx/utils';

import { useCurrentOutlet } from '../outlets/useCurrentOutlet';
import { useUserWebSocket } from '../socket/useUserWebsocket';
import { useStaffOrders } from './useStaffOrders';

interface OrderEventPayload {
  data: Order;
  timestamp: string;
}

interface DeleteEventPayload {
  data: string;
  timestamp: string;
}

export const useStaffOrdersSubscription = () => {
  const { currentOutlet } = useCurrentOutlet();
  const { subscribe, joinRoom, leaveRoom } = useUserWebSocket();
  const queryClient = useQueryClient();
  const { queryKey } = useStaffOrders();

  useEffect(() => {
    if (!currentOutlet) return;

    joinRoom(WS_ROOM_TYPES.OUTLET, currentOutlet.id);

    const unsubCreated = subscribe({
      event: WS_EVENTS.ORDER.CREATED,
      handler: (payload: OrderEventPayload) => {
        console.log('[WebSocket] Received order:created event:', payload);
        queryClient.setQueryData(queryKey, upsertQueryItem(payload.data));
      },
    });

    const unsubUpdated = subscribe({
      event: WS_EVENTS.ORDER.UPDATED,
      handler: (payload: OrderEventPayload) => {
        console.log('[WebSocket] Received order:updated event:', payload);
        queryClient.setQueryData(queryKey, updateQueryItem(payload.data));
      },
    });

    const unsubDeleted = subscribe({
      event: WS_EVENTS.ORDER.DELETED,
      handler: (payload: DeleteEventPayload) => {
        console.log('[WebSocket] Received order:deleted event:', payload);
        queryClient.setQueryData(queryKey, deleteQueryItem(payload.data));
      },
    });

    return () => {
      unsubCreated();
      unsubUpdated();
      unsubDeleted();
      leaveRoom(WS_ROOM_TYPES.OUTLET, currentOutlet.id);
    };
  }, [currentOutlet]);
};
